import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth.jsx";
import Logo from "@/components/Logo";
import { ArrowRight, Mail } from "lucide-react";

export default function ForgotPassword() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="px-6 sm:px-12 py-6 flex items-center justify-between border-b border-neutral-100">
        <Logo />
        <Link to="/login" data-testid="forgot-back-login" className="text-sm text-propera font-semibold hover:underline">Back to Sign In</Link>
      </header>
      <div className="max-w-md mx-auto px-6 py-16">
        {sent ? (
          <div className="text-center" data-testid="forgot-sent-card">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-propera-light text-propera flex items-center justify-center mb-6">
              <Mail size={26} />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-neutral-900 leading-tight">Check your inbox</h1>
            <p className="text-neutral-500 mt-3">
              If an account exists for <b className="text-neutral-900">{email}</b>, we&apos;ve sent a password reset link. The link expires in 1 hour.
            </p>
            <Link to="/login" data-testid="forgot-back-login-cta" className="mt-8 inline-flex items-center justify-center gap-2 bg-propera hover:bg-propera-hover text-white rounded-full px-6 py-3 font-semibold transition">
              Back to Sign In
            </Link>
          </div>
        ) : (
          <>
            <p className="text-xs uppercase tracking-[0.2em] text-propera font-semibold mb-3">Forgot Password</p>
            <h1 className="text-3xl sm:text-4xl font-bold text-neutral-900 leading-tight">Reset your password</h1>
            <p className="text-neutral-500 mt-3">Enter the email you signed up with and we&apos;ll send you a secure reset link.</p>
            <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="forgot-form">
              <div>
                <label className="text-xs font-semibold text-neutral-700">Email</label>
                <input
                  data-testid="forgot-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-1.5 w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none transition"
                />
              </div>
              <button
                data-testid="forgot-submit"
                type="submit"
                disabled={loading || !email}
                className="w-full bg-propera hover:bg-propera-hover text-white rounded-full px-6 py-3.5 font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-60 glow-primary"
              >
                {loading ? "Sending..." : (<>Send Reset Link <ArrowRight size={16} /></>)}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
